import React, { useEffect, useMemo, useState } from 'react';
import { TabContent, TabPane } from 'reactstrap';
import i18next from 'i18next';
import { IRestaurantCompanyWithRestaurantsAndContacts } from 'types';
import { CenterCardSpinner, CollapsibleCard, OtoTabs } from 'app/components/common';
import CompaniesList from 'app/components/companies/CompaniesList';
import CompaniesRestaurantsTable from 'app/components/companies/CompaniesRestaurantsTable';
import AddCompanyForm from 'app/components/companies/AddCompanyForm';
import AddCompanyRestaurantForm from 'app/components/companies/AddCompanyRestaurantForm';
import {
  createCompanyRestaurant,
  createRestaurantCompany,
  loadAdminCompanies,
  selectAdminCompanies,
  selectAdminCompaniesLoading,
  selectAdminRestaurants,
  selectAdminRestaurantsLoading,
} from 'store/admin';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';

const getTabs = () => ({
  Companies: i18next.t('companies.tab-companies'),
  CompaniesRestaurants: i18next.t('companies.tab-companies-restaurants'),
});

const RestaurantCompaniesPage = () => {
  const tabs = useMemo(() => getTabs(), []);
  const [tab, setTab] = useState(tabs.Companies);

  const dispatch = useAppDispatch();
  const companies = useAppSelector(selectAdminCompanies);
  const companiesLoading = useAppSelector(selectAdminCompaniesLoading);
  const restaurants = useAppSelector(selectAdminRestaurants);
  const restaurantsLoading = useAppSelector(selectAdminRestaurantsLoading);

  useEffect(() => {
    dispatch(loadAdminCompanies());
  }, [dispatch]);

  const normalizedCompanies = useMemo(() => {
    return (companies || []).map((item) => {
      const nipVal = item.nip as string | number;
      return {
        ...item,
        nip: typeof nipVal === 'string' ? nipVal.trim() : nipVal,
      };
    });
  }, [companies]);

  const isAnyLoading = companiesLoading || restaurantsLoading;

  if (isAnyLoading && normalizedCompanies.length === 0) {
    return (
      <>
        <CenterCardSpinner text={i18next.t('companies.loading-companies')} />
        {restaurantsLoading && (
          <div>{i18next.t('companies.loading-restaurants')}</div>
        )}
      </>
    );
  }

  return (
    <>
      <OtoTabs activeTab={tab} tabs={tabs} changeTab={setTab} />
      <TabContent activeTab={tab}>
        <TabPane tabId={tabs.Companies}>
          {tab === tabs.Companies && (
            <>
              <CollapsibleCard title={i18next.t('companies.add-company-title')}>
                <AddCompanyForm
                  onAddCompany={(values) =>
                    dispatch(createRestaurantCompany(values))
                  }
                />
              </CollapsibleCard>
              <CompaniesList
                companies={
                  normalizedCompanies as IRestaurantCompanyWithRestaurantsAndContacts[]
                }
              />
            </>
          )}
        </TabPane>
        <TabPane tabId={tabs.CompaniesRestaurants}>
          {tab === tabs.CompaniesRestaurants && (
            <>
              <AddCompanyRestaurantForm
                companies={normalizedCompanies.map((c) => ({
                  id: c.id,
                  name: c.name,
                  nip: c.nip as number,
                }))}
                restaurants={restaurants}
                onAddCompanyRestaurant={(values) =>
                  dispatch(createCompanyRestaurant(values))
                }
              />
              <CompaniesRestaurantsTable
                companies={
                  normalizedCompanies as IRestaurantCompanyWithRestaurantsAndContacts[]
                }
              />
            </>
          )}
        </TabPane>
      </TabContent>
    </>
  );
};

RestaurantCompaniesPage.url = '/admin/restaurant-companies';
RestaurantCompaniesPage.navName = 'admin.companies';
RestaurantCompaniesPage.icon = 'Home';

export default RestaurantCompaniesPage;
